/**
 * Products API
 * 
 * Provides functions for creating products via the WKS Product Entry API.
 * Handles image upload, product creation, and error handling.
 * 
 * Requirements: 1.2, 8.2, 8.3, 8.4
 */

import { getProtectedClient } from './client';
import { uploadImages } from './media';
import type { UploadResult } from './media';
import type { ProductFormData, ImageData } from '../types/product';

/**
 * Product creation request payload
 */
export interface CreateProductRequest {
  name: string;
  description: string;
  short_description: string;
  regular_price: string;
  sale_price: string;
  stock_quantity: number;
  stock_status: string;
  sku: string;
  internal_sku: string;
  supplier_reorder_url: string;
  category_ids: number[];
  brand_id: number;
  attributes: Array<{
    id: number;
    name: string;
    value: string;
    visible: boolean;
  }>;
  ymm_restrictions: Array<{
    make: string;
    model: string;
    yearFrom: number;
    yearTo: number;
  }>;
  image_ids: number[];
}

/**
 * Product creation response
 */
export interface CreateProductResponse {
  id: number;
  name: string;
  sku: string;
  message: string;
}

/**
 * Validation error structure
 */
export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Validate required fields before submission
 * 
 * @param formData - The form data to validate
 * @returns Array of validation errors (empty if valid)
 */
export function validateProductForm(formData: ProductFormData): ValidationError[] {
  const errors: ValidationError[] = [];

  // Required: Product name
  if (!formData.name.trim()) {
    errors.push({ field: 'name', message: 'Product name is required' });
  }

  // Required: Regular price
  if (!formData.regularPrice.trim()) {
    errors.push({ field: 'regularPrice', message: 'Regular price is required' });
  } else if (isNaN(parseFloat(formData.regularPrice)) || parseFloat(formData.regularPrice) < 0) {
    errors.push({ field: 'regularPrice', message: 'Regular price must be a valid positive number' });
  }

  // Optional but validate if provided: Sale price
  if (formData.salePrice.trim()) {
    if (isNaN(parseFloat(formData.salePrice)) || parseFloat(formData.salePrice) < 0) {
      errors.push({ field: 'salePrice', message: 'Sale price must be a valid positive number' });
    } else if (parseFloat(formData.salePrice) >= parseFloat(formData.regularPrice)) {
      errors.push({ field: 'salePrice', message: 'Sale price must be less than regular price' });
    }
  }

  // Validate stock quantity
  if (formData.stockQuantity < 0) {
    errors.push({ field: 'stockQuantity', message: 'Stock quantity cannot be negative' });
  }

  // Validate YMM restrictions
  formData.ymmRestrictions.forEach((ymm, index) => {
    if (ymm.yearFrom && (ymm.yearFrom < 1950 || ymm.yearFrom > 2030)) {
      errors.push({ field: `ymmRestrictions[${index}].yearFrom`, message: `YMM entry ${index + 1}: Year From must be between 1950 and 2030` });
    }
    if (ymm.yearTo && (ymm.yearTo < 1950 || ymm.yearTo > 2030)) {
      errors.push({ field: `ymmRestrictions[${index}].yearTo`, message: `YMM entry ${index + 1}: Year To must be between 1950 and 2030` });
    }
    if (ymm.yearFrom && ymm.yearTo && ymm.yearFrom > ymm.yearTo) {
      errors.push({ field: `ymmRestrictions[${index}]`, message: `YMM entry ${index + 1}: Year From cannot be greater than Year To` });
    }
  });

  return errors;
}

/**
 * Transform form data to API request format
 */
function transformFormDataToRequest(
  formData: ProductFormData,
  imageIds: number[]
): CreateProductRequest {
  return {
    name: formData.name.trim(),
    description: formData.description.trim(),
    short_description: formData.shortDescription.trim(),
    regular_price: formData.regularPrice.trim(),
    sale_price: formData.salePrice.trim(),
    stock_quantity: formData.stockQuantity,
    stock_status: 'instock', // Always in stock
    sku: formData.woocommerceSku.trim(),
    internal_sku: formData.internalSku.trim(),
    supplier_reorder_url: formData.supplierReorderUrl.trim(),
    category_ids: formData.categoryIds,
    brand_id: formData.brandId || 0,
    attributes: formData.attributes.map(attr => ({
      id: attr.id,
      name: attr.name,
      value: attr.value,
      visible: attr.visible,
    })),
    ymm_restrictions: formData.ymmRestrictions.map(ymm => ({
      make: ymm.make,
      model: ymm.model,
      yearFrom: ymm.yearFrom,
      yearTo: ymm.yearTo,
    })),
    image_ids: imageIds,
  };
}

/**
 * Extract files from ImageData array that need uploading
 */
function getFilesToUpload(images: ImageData[]): File[] {
  return images
    .filter(img => img.file && !img.uploaded)
    .map(img => img.file as File);
}

/**
 * Create a product with images
 * 
 * Uploads images first, then creates the product with all data.
 * 
 * @param formData - The product form data
 * @param onImageProgress - Optional callback for image upload progress
 * @returns Promise resolving to created product response
 * @throws Error with specific message on failure
 */
export async function createProduct(
  formData: ProductFormData,
  onImageProgress?: (progress: number) => void
): Promise<CreateProductResponse> {
  // Validate form data
  const validationErrors = validateProductForm(formData);
  if (validationErrors.length > 0) {
    const errorMessages = validationErrors.map(e => e.message).join('; ');
    throw new Error(`Validation failed: ${errorMessages}`);
  }

  // Collect image IDs (already uploaded + new uploads)
  const imageIds: number[] = [];
  
  // Add already uploaded image IDs
  formData.images.forEach(img => {
    if (img.uploaded && img.mediaId) {
      imageIds.push(img.mediaId);
    }
  });

  // Upload new images
  const filesToUpload = getFilesToUpload(formData.images);
  if (filesToUpload.length > 0) {
    try {
      const uploadResults: UploadResult[] = await uploadImages(
        filesToUpload,
        onImageProgress
      );
      uploadResults.forEach(result => imageIds.push(result.id));
    } catch (error) {
      throw new Error(
        `Image upload failed: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`
      );
    }
  }

  // Transform form data to API request
  const requestData = transformFormDataToRequest(formData, imageIds);

  // Create product
  try {
    const client = getProtectedClient();
    const response = await client.post<{ success: boolean; data: CreateProductResponse }>('/products', requestData);
    return response.data.data;
  } catch (error) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
      const status = axiosError.response?.status;
      const message = axiosError.response?.data?.message;

      if (status === 401) {
        throw new Error('Session expired. Please log in again.');
      }
      if (status === 403) {
        throw new Error('You do not have permission to create products.');
      }
      if (message) {
        throw new Error(message);
      }
    }
    throw new Error('Failed to create product. Please try again.');
  }
}
